// Vue & Plugins
import { MaybeRefOrGetter, toValue } from 'vue'

// Composables
import { useQuery, keepPreviousData, useInfiniteQuery, useMutation } from '@tanstack/vue-query'

// Models
import { SiteID } from '../../sites/models/site'
import { Pagination } from '../../common/models/pagination'
import { Shift, ShiftID } from '../models/shift'

// Services
import { ShiftSearchForm, fetch, generateExport } from '../shiftService'

// Other
import { DateTime } from 'luxon'
import { Area } from '../../areas/models/area'
import { Skill } from '../../skills/models/skill'
import { Employee, EmployeeID } from '../../employees/models/employee'
import { Building } from '../../buildings/models/building'
import { ShiftOfferStatus } from '../../employee_shift/models/employeeShiftOffer'
import { useEventStore } from '../../common/eventStore'
import { isThrottledError } from '../../axiosInstance'

export interface ShiftFilters {
    dateFrom?: MaybeRefOrGetter<DateTime>
    dateTo?: MaybeRefOrGetter<DateTime>
    byCreatedDate?: MaybeRefOrGetter<boolean>

    status?: MaybeRefOrGetter<'active' | 'cancelled' | 'closed'>
    fillStatus?: MaybeRefOrGetter<'all' | 'filled' | 'unfilled' | 'awaiting_approval'>

    inProgress?: MaybeRefOrGetter<boolean | undefined>
    inProgressBuffer?: MaybeRefOrGetter<number>

    hasResponses?: MaybeRefOrGetter<boolean>

    building?: MaybeRefOrGetter<Building | null>
    area?: MaybeRefOrGetter<Area | null>
    skill?: MaybeRefOrGetter<Skill | null>
    creator?: MaybeRefOrGetter<Employee | null>
    employee?: MaybeRefOrGetter<Employee | null>
    employeeId?: MaybeRefOrGetter<EmployeeID | null>
    employeeOfferStatus?: MaybeRefOrGetter<ShiftOfferStatus | null>

    rateMin?: MaybeRefOrGetter<number | null>
    rateMax?: MaybeRefOrGetter<number | null>

    perPage?: MaybeRefOrGetter<number>
    page?: MaybeRefOrGetter<number>
}

export const shiftKeys = {
    all: ['shifts'] as const,
    lists: () => [...shiftKeys.all, 'list'] as const,
    list: (siteId: MaybeRefOrGetter<SiteID>, filters: ShiftFilters) => [...shiftKeys.lists(), siteId, { dateFrom: filters.dateFrom, dateTo: filters.dateTo, perPage: filters.perPage, page: filters.page, inProgress: filters.inProgress, inProgressBuffer: filters.inProgressBuffer, hasResponses: filters.hasResponses, building: filters.building, area: filters.area, skill: filters.skill, status: filters.status, fillStatus: filters.fillStatus, rateMin: filters.rateMin, rateMax: filters.rateMax, creator: filters.creator, employee: filters.employee, employeeId: filters.employeeId, employeeOfferStatus: filters.employeeOfferStatus, byCreatedDate: filters.byCreatedDate }] as const,
    listInfinite: (siteId: MaybeRefOrGetter<SiteID>, filters: ShiftFilters) => [...shiftKeys.lists(), 'infinite', siteId, { dateFrom: filters.dateFrom, dateTo: filters.dateTo, perPage: filters.perPage, page: filters.page, inProgress: filters.inProgress, inProgressBuffer: filters.inProgressBuffer, hasResponses: filters.hasResponses, building: filters.building, area: filters.area, skill: filters.skill, status: filters.status, fillStatus: filters.fillStatus, rateMin: filters.rateMin, rateMax: filters.rateMax, creator: filters.creator, employee: filters.employee, employeeId: filters.employeeId, employeeOfferStatus: filters.employeeOfferStatus, byCreatedDate: filters.byCreatedDate }] as const,
    details: () => [...shiftKeys.all, 'detail'] as const,
    detail: (siteId: MaybeRefOrGetter<SiteID>, id: MaybeRefOrGetter<ShiftID>) => [...shiftKeys.details(), siteId, id] as const,
    listForEmployee: (filters: ShiftFilters) => [...shiftKeys.lists(), 'employee', { perPage: filters.perPage, page: filters.page }] as const,
}

const filtersToForm = (filters: ShiftFilters): ShiftSearchForm => {
    return {
        dateFrom: toValue(filters.dateFrom),
        dateTo: toValue(filters.dateTo),
        byCreatedDate: toValue(filters.byCreatedDate),

        status: toValue(filters.status),
        fillStatus: toValue(filters.fillStatus),

        inProgress: toValue(filters.inProgress) ?? undefined,
        inProgressBuffer: toValue(filters.inProgressBuffer) ?? 0,

        hasResponses: toValue(filters.hasResponses) ?? undefined,

        building: toValue(filters.building),
        area: toValue(filters.area),
        skill: toValue(filters.skill),
        creator: toValue(filters.creator),
        employee: toValue(filters.employee),
        employeeId: toValue(filters.employeeId),
        employeeOfferStatus: toValue(filters.employeeOfferStatus),

        rateMin: toValue(filters.rateMin) ?? undefined,
        rateMax: toValue(filters.rateMax) ?? undefined,

        perPage: toValue(filters.perPage) ?? 15,
        page: toValue(filters.page) ?? 1,
    }
}

export function useShiftQuery(siteId: MaybeRefOrGetter<SiteID>, filters: ShiftFilters = {}, options: { enabled?: MaybeRefOrGetter<boolean> } = {}) {
    return useQuery<{ shifts: Shift[], pagination: Pagination }, Error>({
        queryKey: shiftKeys.list(siteId, filters),
        queryFn: async () => {
            const form = filtersToForm(filters)

            return await fetch(toValue(siteId), form)
        },
        placeholderData: keepPreviousData,
        enabled: options.enabled,
    })
}

export function useShiftQueryInfinite(siteId: MaybeRefOrGetter<SiteID>, filters: ShiftFilters = {}, options: { enabled?: MaybeRefOrGetter<boolean> } = {}) {
    return useInfiniteQuery({
        queryKey: shiftKeys.listInfinite(siteId, filters),
        queryFn: async ({ pageParam }) => {
            const form = filtersToForm(filters)

            form.perPage = 15
            form.page = pageParam ?? 1

            return await fetch(toValue(siteId), form)
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const nextPage = lastPage.pagination.currentPage + 1

            if (nextPage > lastPage.pagination.totalPages) {
                return undefined
            }

            return nextPage
        },
        enabled: options.enabled,
    })
}

export function useShiftExportMutation(siteId: MaybeRefOrGetter<SiteID>) {
    const eventStore = useEventStore()

    return useMutation<void, Error, { form: ShiftFilters }>({
        mutationFn: async (variables) => {
            return generateExport(toValue(siteId), filtersToForm(variables.form))
        },
        onSuccess: async () => {
            eventStore.addToast({ severity: 'success', summary: 'Export Started', detail: 'Your export has been started and will be emailed to you when it is complete.' })
        },
        onError: async (error: Error) => {
            if (isThrottledError(error)) {
                eventStore.addErrorToast({ detail: error.message })
            }
        }
    })
}